import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'

import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import AspectRatio from 'components/Common/AspectRatio'
import { connect } from 'react-redux'
import { ImageParams } from 'components/Common/ResponsiveImage'
import PrimaryButtonPaletteProvider from 'contexts/PrimaryButtonPaletteProvider'
import PaletteProvider from 'contexts/PaletteProvider'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import HeroTourOfferDetails from './HeroTourOfferDetails'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { getApplicableVelocityFeatures } from 'lib/partnershipUtils'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'

const Root = styled.div`
  display: grid;
  position: relative;
  height: 100%;
  grid-template-rows: min-content 1fr;

  ${mediaQueryUp.md} {
    grid-template-columns: 67% 1fr;
    grid-template-rows: 1fr;
  }
`

const MainImageSegment = styled.div`
  display: grid;
  position: relative;
`

const OfferOverlayContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(12)};
  left: ${rem(12)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

interface MappedProps {
  iOS: boolean;
  isVelocityEnabled: boolean;
}

interface Props {
  offer: Tours.V2Offer | Tours.V2OfferSummary;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
}

const imageParams: ImageParams = {
  xs: '3:2',
  sm: '16:9',
  mdWidth: '67vw',
  lgWidth: '750px',
  quality: 'best',
}

function HeroTourOfferTile(props: Props & MappedProps) {
  const {
    offer,
    eagerLoadFirstImage,
    isVelocityEnabled,
  } = props

  const { cheapestVariation } = useCheapestOfTourV2Offer(offer)

  const velocityFeatures = useMemo(() => {
    // If at least one purchaseable option is satisfied return true
    const result = offer.purchasableOptions.some((option) => {
      const features = getApplicableVelocityFeatures(option.price)
      return features.canBurn
    })
    return { canEarn: true, canBurn: result }
  }, [offer.purchasableOptions])

  return (
    <PrimaryButtonPaletteProvider palette="default">
      <Root>
        <MainImageSegment>
          <AspectRatio xs="3:2" sm="16:9" fillHeight>
            <ImageCarousel
              images={cheapestVariation?.images || []}
              imageParams={imageParams}
              eagerLoadFirstImage={eagerLoadFirstImage}
            />
          </AspectRatio>
          {isVelocityEnabled && (
            <OfferOverlayContainer>
              <PartnershipLabel canEarn={velocityFeatures.canEarn} canUse={velocityFeatures.canBurn} />
            </OfferOverlayContainer>
          )}
          <TripGuard>
            <CSSBreakpoint max="sm">
              <PaletteProvider type="default">
                <FloatingBookmarkContainer>
                  <BookmarkButton offer={offer} />
                </FloatingBookmarkContainer>
              </PaletteProvider>
            </CSSBreakpoint>
          </TripGuard>
        </MainImageSegment>
        <CSSBreakpoint max="sm">
          <HeroTourOfferDetails
            offer={offer}
            isDesktop={false}
          />
        </CSSBreakpoint>
        <CSSBreakpoint min="md">
          <HeroTourOfferDetails
            offer={offer}
            isDesktop
          />
        </CSSBreakpoint>
      </Root>
    </PrimaryButtonPaletteProvider>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  iOS: state.config.userAgent.osName === 'iOS',
  isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
})

export default connect(mapStateToProps)(HeroTourOfferTile)
